import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

const STORAGE_KEY = 'cnt-analytics';
const ANALYTICS_IDB_KEY = 'analytics:events';
const BUFFER_MAX = 500;
/** track() is the highest write-frequency call site in the app (every
 *  tracked event) — batch writes behind a short timer instead of one IDB
 *  transaction per event, since IDB's per-transaction overhead makes that
 *  worth avoiding here specifically (unlike the cheap synchronous
 *  localStorage.setItem this replaces). */
const FLUSH_DELAY_MS = 300;

export interface IAnalyticsEvent {
  event: string;
  ts: number;
  [prop: string]: unknown;
}

/**
 * Minimal analytics shim. Buffers events (in memory, flushed to IndexedDB
 * on a short debounce) and console-logs them for dev inspection. Real
 * vendor (GA4 / PostHog / Plausible) plugs in behind `track()` later
 * without touching call sites.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private platformId = inject<object>(PLATFORM_ID);

  private readonly store = createIdbKvStore<IAnalyticsEvent[]>({
    legacyKeys: { [ANALYTICS_IDB_KEY]: STORAGE_KEY },
  });

  private buffer: IAnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      void this.store.init().then(() => {
        // Seed from whatever's already persisted, merged ahead of anything
        // tracked (into `this.buffer`) before init() resolved, so an early
        // event doesn't get lost or reordered before real history.
        this.buffer = [...(this.store.read(ANALYTICS_IDB_KEY) ?? []), ...this.buffer].slice(-BUFFER_MAX);
      });
    }
  }

  track(event: string, props: Record<string, unknown> = {}): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const payload: IAnalyticsEvent = { event, ...props, ts: Date.now() };
    console.log('[analytics]', payload);
    this.buffer = [...this.buffer, payload].slice(-BUFFER_MAX);
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.store.write(ANALYTICS_IDB_KEY, this.buffer);
    }, FLUSH_DELAY_MS);
  }
}
